import { CATALOG_ITEM_KINDS, CATALOG_RESOURCES, catalogResource } from "@authe/fe-commons/catalog";
import type { ResourceReadPolicy } from "@authe/fe-commons/events";

/** One inventory for query invalidation and CI coverage. New read families must be classified. */
export interface QueryResourcePolicy {
  policy: ResourceReadPolicy | ((key: readonly unknown[]) => ResourceReadPolicy);
  /** Other local mutation adapters reuse these groups, never their own key lists. */
  group?: "address";
}
const catalog: QueryResourcePolicy = { policy: { resources: CATALOG_RESOURCES } };
const address: QueryResourcePolicy = { ...catalog, group: "address" };
const byKind: QueryResourcePolicy = { policy: (key) => ({ resources:
  CATALOG_ITEM_KINDS.includes(key[1] as typeof CATALOG_ITEM_KINDS[number]) ? [catalogResource(key[1] as typeof CATALOG_ITEM_KINDS[number])] : [] }) };
const independent = (reason: string): QueryResourcePolicy => ({ policy: { independent: reason } });

export const queryResourcePolicies: Readonly<Record<string, QueryResourcePolicy>> = {
  "asset-pricing": independent("Public price observations and pricing job state; separate from catalog data."),
  "catalog-item": byKind,
  "catalog-rows": byKind,
  "catalog-names": byKind,
  "catalog-chains": byKind,
  "catalog-field-overrides": byKind,
  "catalog-related": catalog,
  "catalog-attached-entities": catalog,
  "catalog-attachment-count": catalog,
  "catalog-addresses-attached": catalog,
  "catalog-addresses-headline": catalog,
  "entity-memberships": catalog,
  // Who chose each chain's headline for an entity (`/catalog-primary`, every
  // chain): a `catalog.primary` write changes it, and so does the entity or an
  // address it names moving or going — the same breadth as the memberships.
  "entity-primary-choices": catalog,
  // A structure is entity rows (any of them can move or be renamed), so any
  // catalog change refreshes it; the parent search is a list of the same rows.
  "entity-structure": catalog,
  "entity-parent-search": catalog,
  "entity-pool-probe": catalog,
  "protocol-entities": catalog,
  "protocol-entity-search": catalog,
  "entity-protocol-catalogue": independent(
    "Protocol configuration references from metadata-registry, refreshed on mount and focus; catalog rows are read separately.",
  ),
  "tx-catalog-batch": catalog,
  // GraphQL resolves attribution server-side, so refresh these rows on catalog changes too.
  "recent-tx-feed": catalog,
  "stored-transaction-search": catalog,
  "tx-exact-lookup": catalog,
  "tx-tenant-triage": catalog,
  "graph-assign-entities": catalog,
  // One entry per (chain, address); the batch is a transport detail, not a
  // cache identity — see `features/overlays/addressOverlayRefCache`.
  "address-overlay-ref": address,
  "address-overlays": address,
  "address-overlays-rest": address,
  "address-cross-chain": address,
  "address-cross-chain-stored": address,
  "address-detail": address,
  "address-row": address,
  "addresses-list": address,
  "tx-addr-row": address,
  "address-assignments": address,
  "tx-addr-assignments": address,
  "entity-mapping": address,
  "address-primary-chains": address,
  "address-entity-meta": address,
  // One entry per (chain, token); the batch is a transport detail, the same way
  // `address-overlay-ref` above is — see `features/tokens/tokenMetadataRefCache`.
  //
  // Independent of the catalog, unlike every overlay family: this is what a
  // token IS (its decimals, its ticker), read from token-mgmt-api's global
  // definitions. Renaming an entity or attaching a tag changes who an address
  // is, never what an amount is denominated in — and a re-read on every catalog
  // edit would be the broadest invalidation in the app for data that does not
  // change at all.
  "token-metadata-ref": independent(
    "A token's decimals and ticker, from its global definition — not catalog attribution.",
  ),
  "token-overrides": independent(
    "Scoped token metadata corrections from token-mgmt; refreshed when the editor opens and invalidated after saving, independently of catalog attribution.",
  ),
  // The pricing/poisoning evidence read on an address page: stored transactions
  // and their bodies, judged by the pipeline's own decode. A catalog edit
  // (renaming an entity, attaching a tag) changes none of it.
  "address-stored-evidence": independent(
    "Stored transactions and their decoded bodies for one address, not catalog data.",
  ),
  // Which chains a wallet has acted on, probed so a case can offer to follow
  // the ones it does not fetch. The answer is the chain registry's view of
  // on-chain activity; naming or attaching an entity does not change it.
  "case-active-chains": independent(
    "Chains an address is active on, read from the chain rollup — not catalog data.",
  ),
  // Which of a cluster's members the platform has actually ingested. Run-index
  // coverage, not attribution: renaming the cluster's entity does not change
  // which members have transactions on file.
  "address-cluster-coverage": independent(
    "Run-index coverage per cluster member, not catalog attribution.",
  ),
  // How a research request ended (status, rows sent to Review), read once the
  // review list it opened onto comes back empty. Research-API job state with no
  // resource event of its own; its 30s staleTime is the refresh.
  "research-request-outcome": independent(
    "A research request's status and review-queue count, from the research API — not catalog data.",
  ),
  "notification-scope": independent("Organization, tenant and member choices, not catalog data."),
  "subscriptions": independent("Billing subscription state."),
  "grouping": independent("Rule definitions and executable specs, not catalog groups."),
  "pipelines": independent("Pipeline definitions for reruns."),
  "tx-header": independent("Objective first-paint header and notes; catalog is read by transaction detail/batches."),
  "tx-filter-capability": independent("Platform capability of the list's index-backed filters (the persistence.filter_indexes pipeline setting), not catalog data."),
  "decode-gap-proxy-batch": independent("Contract implementation/code probes."),
  "decode-gap-abi": independent("Contract ABI probes."),
  "contract-is-code": independent("On-chain bytecode probe."),
  // Which catalog rung holds a contract's name: a catalog read, stale the moment a name is saved.
  "contract-name-rung": catalog,
  "pool-contract-composition": independent("On-chain pool getters (token0/token1/fee); immutable, not catalog data."),
  "token-owners": independent("Live provider holder balances; holder labels are resolved by the graph catalog read beside it."),
  "nft-collection-owners": independent("Live provider page of the wallets holding a collection; not stored."),
  "nft-collection-sales": independent("Live provider sale prices for a collection; a market claim, not our observation."),
  "token-holder-stats": independent("Live provider count of every holder (total, top-N supply, change windows); not stored, not catalog data."),
  "onchain-feed": independent("Blockchain rows and topology; current catalog is resolved by overlay batches."),
  "org-roles": independent("Organization authorization roles."),
  "org-users": independent("Organization member directory."),
  "my-teams": independent("Team membership directory."),
  "quick-start-work": independent("Identity-scoped facts already loaded by the dashboard queue; no separate inventory read."),
  "team-memberships": independent("Identity-scoped team authorization; refreshes on focus and every minute independently of streaming."),
  "collaboration-roster": independent("Identity/team-scoped mention roster; refreshes on focus and every minute."),
  "address-cluster": independent("Heuristic cluster structure, not catalog attribution."),
  // The case report's cluster sentence per followed address — the same
  // heuristic lookups as `address-cluster`, batched for the report.
  "case-report-clusters": independent("Heuristic cluster lookups for the case report, not catalog attribution."),
  "actor-identities": independent("Related-addresses identity membership lookup; catalog attribution is read separately."),
  "address-cluster-evidence": independent("Heuristic cluster evidence."),
  "address-cluster-roles": independent("Heuristic role assignments."),
  "address-cluster-roles-for": independent("Heuristic role assignments."),
  "cluster-overrides": independent("Authored cluster overrides."),
  "seed-reviews": catalog,
  "usage": independent("Billing usage counters."),
  "config-snapshot": independent("Versioned operator configuration snapshot, isolated by full tenancy and refreshed every minute."),
  "feature-flags": independent("Operator-set customer feature flags, read once per tenancy."),
  "enforced-policy": independent("Operator-set visibility policy (admin portal only), read once per tenancy and refreshed every minute."),
  "team-roles": independent("Team authorization roles."),
  "team-capabilities": independent("Active team membership and team-role grants, not catalog data."),
};

export function queryDependsOnResources(key: readonly unknown[], resources: ReadonlySet<string>): boolean {
  const entry = queryResourcePolicies[String(key[0])];
  if (!entry) return false;
  const policy = typeof entry.policy === "function" ? entry.policy(key) : entry.policy;
  return policy.resources?.some((resource) => resources.has(resource)) ?? false;
}

export const ADDRESS_QUERY_PREFIXES: readonly (readonly [string])[] = Object.entries(queryResourcePolicies)
  .filter(([, entry]) => entry.group === "address").map(([key]) => [key] as const);
